<template>
    <div v-if="showButton" class="add-cart__container" :class="`${addBasketAlign}`">
        <button
            v-show="basketAmount === 0"
            :class="`btn ${buttonClass} rounded-25`"
            type="submit"
            data-cy="add-to-cart"
            @click="changeProductAmount(1)"
        >
            {{ $t(isPreorder ? 'product.preorder' : 'product.addToCard') }}
        </button>
        <div v-show="basketAmount !== 0" class="js-amount-positive add-cart">
            <button
                :class="`btn btn-outline-grey add-cart__change btn-minus`"
                type="submit"
                data-cy="add-cart__change-minus"
                @click="changeProductAmount(basketAmount - 1)"
            >
                -
            </button>
            <input
                v-model="basketAmount"
                data-cy="inputCounterProduct"
                class="add-cart__stock add-cart__stock--detail"
                type="number"
                min="1"
                max="99"
                @change="inputAmountChanged"
            />

            <ObjectsTextTooltip v-if="basketAmount >= maxAmountPerOrderFinal" :text="maxAmountPerOrderText">
                <button :class="`btn btn-green add-cart__change btn-plus`" :disabled="basketAmount >= maxAmountPerOrderFinal" type="submit">
                    +
                </button>
            </ObjectsTextTooltip>
            <button
                v-else
                :class="`btn btn-green add-cart__change btn-plus`"
                type="submit"
                data-cy="add-cart__change-plus"
                @click="changeProductAmount(basketAmount + 1)"
            >
                +
            </button>
        </div>
        <div
            v-if="!loading && showActualStateMode !== 'hidden' && basketAmount > 0 && alertCartShow && !addonSaleBehavior"
            class="alert-addcart my-3"
            :class="`alert-addcart__${showActualStateMode}`"
        >
            <div v-html="$t('product.basket', { basketAmount: basketAmount, totalPrice: $formatCurrency(totalPrice) })"></div>
        </div>
    </div>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n'
import { PropType } from 'vue'
import { useBasketStore } from '~/stores/basket'
import { addProductToBasket } from '~/utils/addProductToBasket'
import Product from '~/models/Product/Product'
import FullProduct from '~/models/Product/FullProduct'
import BasketProduct from '~/models/Basket/BasketProduct'

export default {
    inject: {
        productIdReferrer: {
            from: 'productIdReferrer',
            default: null,
        },
    },
    props: {
        // products
        product: {
            type: Object as PropType<Product>,
            default: null,
        },
        fullProduct: {
            type: Object as PropType<FullProduct>,
            default: null,
        },
        basketProduct: {
            type: Object as PropType<BasketProduct>,
            default: null,
        },

        // product properties
        productId: {
            type: Number,
            required: true,
        },
        productPrice: {
            type: Number,
            required: true,
        },
        isPreorder: {
            type: Boolean,
            required: true,
        },
        maxAmountPerOrder: {
            type: Number,
            required: true,
        },
        hasSubstitutionModal: {
            type: Boolean,
            required: true,
        },

        // general properties
        buttonClass: {
            type: String,
            default: 'btn-primary',
        },
        addBasketAlign: {
            type: String,
            default: 'text-end',
        },
        showPreBasket: {
            type: Boolean,
            default: true,
        },
        alertCartShow: {
            type: Boolean,
            default: true,
        },

        // GTM properties
        listPosition: {
            type: Number,
            default: null,
        },
        listTitle: {
            type: String,
            default: '',
        },
        listType: {
            type: String,
            default: '',
        },
        itemListName: {
            type: String,
            default: '',
        },
        carouselName: {
            type: String,
            default: '',
        },
        showActualStateMode: {
            type: String,
            default: 'hidden',
        },
        addonSaleBehavior: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['productAddedToBasket'],

    data() {
        return {
            maxAmountDefault: 99,
            loading: false,
        }
    },

    computed: {
        maxAmountPerOrderText() {
            return this.maxAmountPerOrder < this.maxAmountDefault
                ? this.$t('basic.maxAmountPerOrder')
                : this.$t('basic.maxAmount', { n: this.maxAmountPerOrder })
        },

        totalPrice() {
            const basketStore = useBasketStore()

            return basketStore.calculateProductPriceAfterDiscount(this.productId)
        },

        maxAmountPerOrderFinal() {
            return this.maxAmountPerOrder || this.maxAmountDefault
        },

        basketAmount() {
            const basketStore = useBasketStore()
            return basketStore.productAmounts[this.productId] || 0
        },

        showButton() {
            return useI18n().locale.value !== 'ro'
        },
    },

    mounted() {
        useNuxtApp().$bus.$on('productAddedToBasket', () => {
            this.loading = false
        })
    },

    methods: {
        inputAmountChanged(event: any): void {
            this.changeProductAmount(parseInt(event.target.value))
        },

        changeProductAmount(amount: number): void {
            const { listTitle, listType, listPosition, itemListName, carouselName, productId, showPreBasket, productIdReferrer } = this

            // substitutions removed due to legal issues - IS-6888
            /* if (this.basketAmount === 0 && this.hasSubstitutionModal) {
                useNuxtApp().$bus.$emit('showProductSubstitutionsModal')
                return
            } */

            addProductToBasket({
                product: this.product,
                fullProduct: this.fullProduct,
                basketProduct: this.basketProduct,
                productId,
                amount,
                showPreBasket,
                itemListName,
                carouselName,
                listTitle,
                listType,
                listPosition,
                productIdReferrer,
                addonSaleBehavior: this.addonSaleBehavior,
            })

            let url = ''

            if (this.product) {
                url = this.product.url
            } else if (this.fullProduct) {
                url = this.fullProduct.url
            } else if (this.basketProduct) {
                url = this.basketProduct.url
            }

            this.loading = true
            this.$emit('productAddedToBasket', url)
        },
    },
}
</script>

<style lang="scss" scoped>
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

input[type='number'] {
    -moz-appearance: textfield;
}

.counter {
    display: none;
}

.alert-addcart {
    display: flex;
    justify-content: center;
    left: 0;
    bottom: calc(100% - 150px);
    color: $color-dark;
    padding: 0 0.3em;

    @include screen-md {
        width: 100%;
        bottom: calc(100% - 190px);
    }

    &__big {
        display: block;
        text-align: center;

        & > div {
            color: $color-dark;
            font-size: 14px;
            background-color: $color-green;
            border-radius: 10px;
            padding: 0.5em 1em;
            left: 10px;
            right: 10px;
            width: auto;
            font-weight: 500;
        }
    }

    &__small > div {
        font-size: 11px;
        display: inline-block;
        background-color: rgba(233, 235, 178, 0.8);
        border-radius: 10px;
        padding: 0.5em 0.7em;
        font-weight: 500;
        text-align: center;
        white-space: nowrap;
        text-wrap: wrap;

        @include screen-md {
            font-size: 13px;
            padding: 0.5em 1em;
        }
    }
}

.add-cart {
    max-width: 160px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #fff;
    border-radius: 50px;
    padding: 1px;
    border: 1px solid #e6e9f0;

    &.add-cart__md {
        width: 120px;
    }

    &__container--lg {
        .btn {
            max-width: 250px;
        }

        .add-cart {
            width: 100%;
            max-width: 180px;
        }

        .btn-primary {
            height: 39px;
            margin-bottom: 5px;
        }

        .add-cart__change {
            width: 40px;
            height: 39px;
            flex: 0 0 40px;

            &.btn-plus {
                width: 70px;
                height: 39px;
                flex: 0 0 70px;

                &:disabled {
                    background: #ddd;
                    border: 1px solid #ddd;

                    &:hover {
                        cursor: not-allowed;
                        background: #ddd;
                        border: 1px solid #ddd;
                    }
                }
            }
        }
    }

    &__container--max {
        width: 100%;

        .btn {
            max-width: 100%;
        }

        .add-cart {
            width: 100%;
        }

        .btn-primary {
            height: 39px;
        }

        .add-cart__change {
            width: 40px;
            height: 39px;
            flex: 0 0 40px;

            &.btn-plus {
                width: 70px;
                height: 39px;
                flex: 0 0 70px;

                &:disabled {
                    background: #ddd;
                    border: 1px solid #ddd;

                    &:hover {
                        cursor: not-allowed;
                        background: #ddd;
                        border: 1px solid #ddd;
                    }
                }
            }
        }
    }

    &__container .add-cart__container {
        display: none;
    }

    &__simple {
        font-size: 14px;
        min-width: 120px;
        font-weight: 500;
        white-space: nowrap;
        width: 100%;
        max-width: 140px;
        padding: 3px 20px 5px;

        &.add-cart__md {
            font-size: 13px;
            width: auto;
            min-width: auto;
            padding: 6px 14px;
        }
    }

    label {
        padding: 0 6px;
        line-height: 31px;
        margin: 0;
        font-size: 16px;
        width: 50%;
        box-sizing: border-box;
        text-align: left;
    }

    &__change {
        width: 31px;
        flex: 0 0 31px;
        height: 31px;
        line-height: 31px;
        font-size: 18px;
        padding: 0 0 3px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.btn-minus {
            //background-color: transparent;
            //color: $color-cta;

            //&:hover {
            //    @media (min-width: 1024px) {
            //        background-color: $color-cta;
            //        color: $color-white;
            //    }
            //}
        }

        &.btn-plus {
            width: 31px;
            height: 31px;
            flex: 0 0 31px;
        }
    }

    &__stock {
        &:focus {
            outline: none;
        }

        &--detail {
            width: 100%;
            line-height: 29px;
            font-size: 16px;
            border: 1px solid rgb(0 0 0 / 0%);
            border-radius: 3px;
            margin-left: 2px;
            padding: 0 0.25em;
            text-align: center;
            background: transparent;
        }
    }
}
</style>
